import { useMemo, useState, type ReactNode } from 'react';

import { Empty } from './Ui.tsx';

export interface Column<T> {
  /** Stable identity for sorting state. */
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  /**
   * Value to sort on. Omit to make the column unsortable - useful for one that
   * is purely a visual indicator.
   */
  sortValue?: (row: T) => string | number | null | undefined;
  /** Text the search box matches against. */
  searchText?: (row: T) => string;
  align?: 'left' | 'num';
  width?: string;
}

export interface DataTableProps<T> {
  rows: readonly T[];
  columns: Array<Column<T>>;
  rowKey: (row: T) => string;
  initialSort?: { key: string; dir: 'asc' | 'desc' };
  searchPlaceholder?: string;
  emptyTitle?: string;
  emptyBody?: ReactNode;
  /** Rows shown before the "show more" control. Omit for no limit. */
  pageSize?: number;
  toolbar?: ReactNode;
  /**
   * Suppresses the built-in search box.
   *
   * For a page that already searches across more than one view, two search
   * boxes on screen is one too many - the page-level one has to win, because it
   * is the only one the tile view can also obey.
   */
  hideSearch?: boolean;
}

/**
 * Sortable, searchable table.
 *
 * Sorting is type-aware: numbers compare numerically, strings
 * case-insensitively with natural number ordering, and null or empty values
 * always sort last regardless of direction. That last rule matters - an item
 * with no benchmark is missing data, not the cheapest thing on the list, and
 * letting it float to the top of an ascending price sort would be misleading.
 */
export function DataTable<T>({
  rows,
  columns,
  rowKey,
  initialSort,
  searchPlaceholder = 'Search',
  emptyTitle = 'Nothing to show',
  emptyBody,
  pageSize,
  toolbar,
  hideSearch = false,
}: DataTableProps<T>) {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(
    initialSort ?? null,
  );
  const [expanded, setExpanded] = useState(false);

  const searchable = useMemo(() => columns.filter((c) => c.searchText), [columns]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle || searchable.length === 0) return rows;
    return rows.filter((row) =>
      searchable.some((column) => column.searchText!(row).toLowerCase().includes(needle)),
    );
  }, [rows, query, searchable]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const column = columns.find((c) => c.key === sort.key);
    if (!column?.sortValue) return filtered;

    const direction = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const left = column.sortValue!(a);
      const right = column.sortValue!(b);

      const leftMissing = left == null || left === '';
      const rightMissing = right == null || right === '';
      if (leftMissing && rightMissing) return 0;
      if (leftMissing) return 1;
      if (rightMissing) return -1;

      if (typeof left === 'number' && typeof right === 'number') {
        return (left - right) * direction;
      }
      return String(left).localeCompare(String(right), undefined, { numeric: true }) * direction;
    });
  }, [filtered, sort, columns]);

  const visible = pageSize && !expanded ? sorted.slice(0, pageSize) : sorted;
  const hidden = sorted.length - visible.length;

  const toggleSort = (column: Column<T>) => {
    if (!column.sortValue) return;
    setSort((current) => {
      if (current?.key !== column.key) {
        // Numeric columns are almost always most interesting largest-first.
        return { key: column.key, dir: column.align === 'num' ? 'desc' : 'asc' };
      }
      return { key: column.key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
    });
  };

  return (
    <>
      {((searchable.length > 0 && !hideSearch) || toolbar) && (
        <div className="table-controls">
          {searchable.length > 0 && !hideSearch && (
            <input
              type="search"
              value={query}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              onChange={(event) => {
                setQuery(event.target.value);
                setExpanded(false);
              }}
            />
          )}
          <div className="table-controls-right">
            {query !== '' && (
              <span className="small muted">
                {sorted.length} of {rows.length}
              </span>
            )}
            {toolbar}
          </div>
        </div>
      )}
      <TableBody
        columns={columns}
        rowKey={rowKey}
        visible={visible}
        sorted={sorted}
        hidden={hidden}
        query={query}
        sort={sort}
        emptyTitle={emptyTitle}
        emptyBody={emptyBody}
        onClearSearch={() => setQuery('')}
        onToggleSort={toggleSort}
        onExpand={() => setExpanded(true)}
      />
    </>
  );
}

interface TableBodyProps<T> {
  columns: Array<Column<T>>;
  rowKey: (row: T) => string;
  visible: readonly T[];
  sorted: readonly T[];
  hidden: number;
  query: string;
  sort: { key: string; dir: 'asc' | 'desc' } | null;
  emptyTitle: string;
  emptyBody: ReactNode;
  onClearSearch: () => void;
  onToggleSort: (column: Column<T>) => void;
  onExpand: () => void;
}

function TableBody<T>({
  columns,
  rowKey,
  visible,
  sorted,
  hidden,
  query,
  sort,
  emptyTitle,
  emptyBody,
  onClearSearch,
  onToggleSort,
  onExpand,
}: TableBodyProps<T>) {
  if (sorted.length === 0) {
    return (
      <Empty title={query ? 'Nothing matches that search' : emptyTitle}>
        {query ? (
          <button className="ghost" onClick={onClearSearch}>
            Clear search
          </button>
        ) : (
          emptyBody
        )}
      </Empty>
    );
  }

  return (
    <>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              {columns.map((column) => {
                const active = sort?.key === column.key;
                const arrow = active ? (sort!.dir === 'asc' ? '\u2191' : '\u2193') : '\u2195';
                return (
                  <th
                    key={column.key}
                    className={column.align === 'num' ? 'num' : undefined}
                    style={column.width ? { width: column.width } : undefined}
                    aria-sort={
                      active ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : undefined
                    }
                  >
                    {column.sortValue ? (
                      <button
                        type="button"
                        className={`th-sort ${active ? 'active' : ''}`}
                        onClick={() => onToggleSort(column)}
                      >
                        {column.header}
                        <span className="th-arrow" aria-hidden="true">
                          {arrow}
                        </span>
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={rowKey(row)}>
                {columns.map((column) => (
                  <td key={column.key} className={column.align === 'num' ? 'num' : undefined}>
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hidden > 0 && (
        <div style={{ marginTop: 10, textAlign: 'center' }}>
          <button className="ghost" onClick={onExpand}>
            Show {hidden} more
          </button>
        </div>
      )}
    </>
  );
}
